// ============================================================================
// Cave84 — Appellation inference (single source of truth)
// ============================================================================
// WHY THIS EXISTS
//   The MWC auction import only has a PDF-parsed cuvée string
//   ("Vosne Romanée 1er Cru Les Suchots") — no structured appellation — so it
//   historically wrote `appellation_id: ''`, orphaning every auction wine
//   (no region, no classification, renders "· Other", no region filtering).
//
//   This module derives appellation name / classification_level / region /
//   subregion / country from a cuvée (+ optional producer & style), with a
//   confidence rating. HIGH/MEDIUM commit silently; LOW is surfaced for review.
//
//   Consumed by:
//     - apps/web MWC import (import-time resolution)
//     - scripts backfill (repair existing orphans)
//   Keep this the ONLY place this logic lives.
// ============================================================================

import type { ClassificationLevel } from './index';

export type InferenceConfidence = 'high' | 'medium' | 'low';

export interface AppellationInference {
  /** Appellation-level name, e.g. "Vosne-Romanée 1er Cru" or "Romanée-Saint-Vivant". */
  name: string;
  classification_level: ClassificationLevel;
  country: string;                 // ISO-2
  region: string;                  // canonical, e.g. "Burgundy"
  subregion?: string;              // e.g. "Côte de Nuits"
  confidence: InferenceConfidence;
  /** What token drove the match — for the review UI / audit. */
  matchedOn: string;
}

export interface InferOptions {
  producerName?: string;
  style?: string;                  // WineStyle
}

function norm(s: string | undefined): string {
  return (s ?? '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, ''); // strip diacritics for matching
}

// ── Burgundy communes → display name + subregion ────────────────────────────
interface Commune { rx: RegExp; name: string; sub: string; }
const BURGUNDY_COMMUNES: Commune[] = [
  // Côte de Nuits
  { rx: /marsannay/, name: 'Marsannay', sub: 'Côte de Nuits' },
  { rx: /fixin/, name: 'Fixin', sub: 'Côte de Nuits' },
  { rx: /gevrey[ -]*chambertin|gevrey/, name: 'Gevrey-Chambertin', sub: 'Côte de Nuits' },
  { rx: /morey[ -]*st[ .-]*denis|morey[ -]*saint[ -]*denis|morey/, name: 'Morey-Saint-Denis', sub: 'Côte de Nuits' },
  { rx: /chambolle[ -]*musigny|chambolle/, name: 'Chambolle-Musigny', sub: 'Côte de Nuits' },
  { rx: /vougeot/, name: 'Vougeot', sub: 'Côte de Nuits' },
  { rx: /vosne[ -]*romanee|flagey[ -]*echezeaux|vosne/, name: 'Vosne-Romanée', sub: 'Côte de Nuits' },
  { rx: /nuits[ -]*st[ .-]*georges|nuits[ -]*saint[ -]*georges/, name: 'Nuits-Saint-Georges', sub: 'Côte de Nuits' },
  // Côte de Beaune
  { rx: /pernand[ -]*vergelesses|pernand/, name: 'Pernand-Vergelesses', sub: 'Côte de Beaune' },
  { rx: /aloxe[ -]*corton|aloxe/, name: 'Aloxe-Corton', sub: 'Côte de Beaune' },
  { rx: /savigny[ -]*les[ -]*beaune|savigny/, name: 'Savigny-lès-Beaune', sub: 'Côte de Beaune' },
  { rx: /pommard/, name: 'Pommard', sub: 'Côte de Beaune' },
  { rx: /volnay/, name: 'Volnay', sub: 'Côte de Beaune' },
  { rx: /monthelie/, name: 'Monthélie', sub: 'Côte de Beaune' },
  { rx: /auxey[ -]*duresses|auxey/, name: 'Auxey-Duresses', sub: 'Côte de Beaune' },
  { rx: /meursault/, name: 'Meursault', sub: 'Côte de Beaune' },
  { rx: /puligny[ -]*montrachet|puligny/, name: 'Puligny-Montrachet', sub: 'Côte de Beaune' },
  { rx: /chassagne[ -]*montrachet|chassagne/, name: 'Chassagne-Montrachet', sub: 'Côte de Beaune' },
  { rx: /st[ .-]*aubin|saint[ -]*aubin/, name: 'Saint-Aubin', sub: 'Côte de Beaune' },
  { rx: /santenay/, name: 'Santenay', sub: 'Côte de Beaune' },
  { rx: /maranges/, name: 'Maranges', sub: 'Côte de Beaune' },
  { rx: /beaune/, name: 'Beaune', sub: 'Côte de Beaune' },
  // Chablis / Chalonnaise / Mâconnais
  { rx: /chablis/, name: 'Chablis', sub: 'Chablis' },
  { rx: /givry/, name: 'Givry', sub: 'Côte Chalonnaise' },
  { rx: /mercurey/, name: 'Mercurey', sub: 'Côte Chalonnaise' },
  { rx: /rully/, name: 'Rully', sub: 'Côte Chalonnaise' },
  { rx: /montagny/, name: 'Montagny', sub: 'Côte Chalonnaise' },
  { rx: /pouilly[ -]*fuisse|pouilly/, name: 'Pouilly-Fuissé', sub: 'Mâconnais' },
  { rx: /macon/, name: 'Mâcon', sub: 'Mâconnais' },
];

// ── Named Grand Crus that often appear WITHOUT the words "grand cru" ─────────
interface NamedCru { rx: RegExp; name: string; sub: string; }
const BURGUNDY_GRANDS_CRUS: NamedCru[] = [
  { rx: /romanee[ -]*conti/, name: 'Romanée-Conti', sub: 'Côte de Nuits' },
  { rx: /la[ -]*tache/, name: 'La Tâche', sub: 'Côte de Nuits' },
  { rx: /romanee[ -]*st[ .-]*vivant|romanee[ -]*saint[ -]*vivant/, name: 'Romanée-Saint-Vivant', sub: 'Côte de Nuits' },
  { rx: /richebourg/, name: 'Richebourg', sub: 'Côte de Nuits' },
  { rx: /la[ -]*romanee\b/, name: 'La Romanée', sub: 'Côte de Nuits' },
  { rx: /grands[ -]*echezeaux/, name: 'Grands-Échézeaux', sub: 'Côte de Nuits' },
  { rx: /echezeaux/, name: 'Échézeaux', sub: 'Côte de Nuits' },
  { rx: /clos[ -]*de[ -]*vougeot|clos[ -]*vougeot/, name: 'Clos de Vougeot', sub: 'Côte de Nuits' },
  { rx: /musigny/, name: 'Musigny', sub: 'Côte de Nuits' },
  { rx: /bonnes[ -]*mares/, name: 'Bonnes-Mares', sub: 'Côte de Nuits' },
  { rx: /clos[ -]*de[ -]*tart/, name: 'Clos de Tart', sub: 'Côte de Nuits' },
  { rx: /clos[ -]*de[ -]*la[ -]*roche/, name: 'Clos de la Roche', sub: 'Côte de Nuits' },
  { rx: /clos[ -]*st[ .-]*denis|clos[ -]*saint[ -]*denis/, name: 'Clos Saint-Denis', sub: 'Côte de Nuits' },
  { rx: /clos[ -]*des[ -]*lambrays/, name: 'Clos des Lambrays', sub: 'Côte de Nuits' },
  { rx: /chambertin[ -]*clos[ -]*de[ -]*beze/, name: 'Chambertin-Clos de Bèze', sub: 'Côte de Nuits' },
  { rx: /charmes[ -]*chambertin|mazoyeres/, name: 'Charmes-Chambertin', sub: 'Côte de Nuits' },
  { rx: /mazis[ -]*chambertin/, name: 'Mazis-Chambertin', sub: 'Côte de Nuits' },
  { rx: /ruchottes[ -]*chambertin/, name: 'Ruchottes-Chambertin', sub: 'Côte de Nuits' },
  { rx: /latricieres[ -]*chambertin/, name: 'Latricières-Chambertin', sub: 'Côte de Nuits' },
  { rx: /griotte[ -]*chambertin/, name: 'Griotte-Chambertin', sub: 'Côte de Nuits' },
  { rx: /chapelle[ -]*chambertin/, name: 'Chapelle-Chambertin', sub: 'Côte de Nuits' },
  { rx: /chambertin/, name: 'Chambertin', sub: 'Côte de Nuits' }, // keep last of the -Chambertin family
  // Côte de Beaune whites/reds. The Montrachet family is ordered longest-name
  // first: `.find()` takes the first hit, and the bare /montrachet/ also matches
  // inside every compound name (Chevalier-Montrachet was being filed as
  // Montrachet; Bienvenues-Bâtard as Bâtard).
  { rx: /bienvenues[ -]*batard/, name: 'Bienvenues-Bâtard-Montrachet', sub: 'Côte de Beaune' },
  { rx: /criots[ -]*batard/, name: 'Criots-Bâtard-Montrachet', sub: 'Côte de Beaune' },
  { rx: /chevalier[ -]*montrachet/, name: 'Chevalier-Montrachet', sub: 'Côte de Beaune' },
  { rx: /batard[ -]*montrachet/, name: 'Bâtard-Montrachet', sub: 'Côte de Beaune' },
  { rx: /montrachet\b/, name: 'Montrachet', sub: 'Côte de Beaune' }, // keep last of the -Montrachet family
  { rx: /corton[ -]*charlemagne/, name: 'Corton-Charlemagne', sub: 'Côte de Beaune' },
  { rx: /\bcorton\b/, name: 'Corton', sub: 'Côte de Beaune' },
];

// ── Champagne ───────────────────────────────────────────────────────────────
/**
 * Grande marque + grower houses. Exported because style inference needs the
 * same list — a bottle whose producer is Krug is sparkling AND Champagne, and
 * those two conclusions must never disagree. Match against normalised text
 * (lowercased, diacritics stripped).
 */
export const CHAMPAGNE_HOUSES =
  /\b(bollinger|krug|roederer|moet|pol roger|taittinger|ruinart|veuve clicquot|salon|delamotte|philipponnat|jacquesson|selosse|egly[ -]?ouriet|billecart|laurent[ -]?perrier|gosset|deutz|perrier[ -]?jouet|piper[ -]?heidsieck|charles heidsieck|bruno paillard|agrapart|ulysse collin|cedric bouchard|roses de jeanne|vilmart|pierre peters|larmandier|chartogne|prevost|lassaigne|dhondt|bereche|savart|marguet|henriot|ayala|drappier|duval[ -]?leroy|pommery|mumm|lanson|besserat|paul bara|vazart|suenen|georges laval|emmanuel brochet)\b/;

/** Prestige cuvées that arrive without their house name attached. */
export const CHAMPAGNE_CUVEES =
  /\b(dom perignon|cristal|clos du mesnil|clos d[' ]?ambonnay|grande annee|grand annee|comtes de champagne|belle epoque|sir winston|la grande dame|dames de|fleur de miraval|cuvee william|nicolas francois|grand siecle|blanc de blancs|blanc de noirs|vieilles vignes francaises)\b/;

// ── Germany ─────────────────────────────────────────────────────────────────
/**
 * VDP.Grosse Lage sites of the Mosel/Saar/Ruwer — the German Grand Cru
 * equivalent. Curated deliberately: only sites classified Grosse Lage appear
 * here, so `classification_level: 'grand_cru'` is a safe assertion. Ordinary
 * Einzellagen fall through to `village`.
 *
 * NOTE ON WORD BOUNDARIES: German village adjectives inflect — the site is
 * "Scharzhofberg" but the label says "Scharzhofberger", "Wehlen" → "Wehlener".
 * A trailing `\b` therefore does NOT match; every pattern below allows a `\w*`
 * suffix. The canonical `name` is carried separately so the stored appellation
 * is the SITE ("Wehlener Sonnenuhr"), not the whole cuvée with its Prädikat.
 */
interface MoselSite { rx: RegExp; name: string; sub: 'Mosel' | 'Saar' | 'Ruwer'; }
export const MOSEL_GROSSE_LAGE_SITES: MoselSite[] = [
  // Saar
  { rx: /scharzhofberg\w*/, name: 'Scharzhofberger', sub: 'Saar' },
  { rx: /wiltinger\w*\s+braune\s+kupp|braune\s+kupp/, name: 'Wiltinger Braune Kupp', sub: 'Saar' },
  { rx: /wiltinger\w*\s+gottesfuss/, name: 'Wiltinger Gottesfuß', sub: 'Saar' },
  { rx: /ockfener\w*\s+bockstein|bockstein/, name: 'Ockfener Bockstein', sub: 'Saar' },
  { rx: /saarburger\w*\s+rausch|\brausch\b/, name: 'Saarburger Rausch', sub: 'Saar' },
  { rx: /ayler\w*\s+kupp/, name: 'Ayler Kupp', sub: 'Saar' },
  { rx: /kanzemer\w*\s+altenberg/, name: 'Kanzemer Altenberg', sub: 'Saar' },
  // Ruwer
  { rx: /\babtsberg\b/, name: 'Maximin Grünhäuser Abtsberg', sub: 'Ruwer' },
  { rx: /\bbruderberg\b/, name: 'Maximin Grünhäuser Bruderberg', sub: 'Ruwer' },
  { rx: /karthauserhofberg|\bkaseler\w*\s+nies/, name: 'Karthäuserhofberg', sub: 'Ruwer' },
  // Mosel
  { rx: /wehlener\w*\s+sonnenuhr/, name: 'Wehlener Sonnenuhr', sub: 'Mosel' },
  { rx: /zeltinger\w*\s+sonnenuhr/, name: 'Zeltinger Sonnenuhr', sub: 'Mosel' },
  { rx: /brauneberger\w*\s+juffer[ -]?sonnenuhr/, name: 'Brauneberger Juffer-Sonnenuhr', sub: 'Mosel' },
  { rx: /brauneberger\w*\s+juffer/, name: 'Brauneberger Juffer', sub: 'Mosel' },
  { rx: /graacher\w*\s+himmelreich/, name: 'Graacher Himmelreich', sub: 'Mosel' },
  { rx: /graacher\w*\s+domprobst/, name: 'Graacher Domprobst', sub: 'Mosel' },
  { rx: /josephshof\w*/, name: 'Graacher Josephshöfer', sub: 'Mosel' },
  { rx: /bernkasteler\w*\s+doctor|\bdoctor\b/, name: 'Bernkasteler Doctor', sub: 'Mosel' },
  { rx: /bernkasteler\w*\s+lay/, name: 'Bernkasteler Lay', sub: 'Mosel' },
  { rx: /urziger\w*\s+wurzgarten|wurzgarten/, name: 'Ürziger Würzgarten', sub: 'Mosel' },
  { rx: /erdener\w*\s+pralat/, name: 'Erdener Prälat', sub: 'Mosel' },
  { rx: /erdener\w*\s+treppchen/, name: 'Erdener Treppchen', sub: 'Mosel' },
  { rx: /trittenheimer\w*\s+apotheke/, name: 'Trittenheimer Apotheke', sub: 'Mosel' },
  { rx: /piesporter\w*\s+goldtropfchen|goldtropfchen/, name: 'Piesporter Goldtröpfchen', sub: 'Mosel' },
  { rx: /dhron\w*\s+hofberger/, name: 'Dhroner Hofberger', sub: 'Mosel' },
];

/** Mosel vineyard-name suffixes that identify the region without naming it. */
export const MOSEL_SITES =
  /\b(scharzhofberg|wehlener|graacher|bernkasteler|urziger|erdener|piesporter|brauneberger|wiltinger|ockfener|saarburger|kanzemer|zeltinger|trittenheimer|dhron|kaseler|eitelsbach|ayler|serriger|leiwener|krover|enkircher|winninger|sonnenuhr|wurzgarten|goldtropfchen)\w*/;

export const MOSEL_PRODUCERS =
  /\b(egon muller|le gallais|j\.? ?j\.? ?prum|joh\.? jos\.? prum|prum\b|schloss lieser|van volxem|fritz haag|willi schaefer|selbach|markus molitor|immich|karthauserhof|maximin grunhaus|grunhaus|zilliken|geltz|clemens busch|stein\b|falkenstein|hofgut falkenstein|vollenweider|weiser[ -]?kunstler|lauer\b|peter lauer|heymann[ -]?lowenstein|reinhold haart|st\.? urbans[ -]?hof|kesselstatt)\b/;

function burgundyResult(
  name: string, sub: string, cls: ClassificationLevel,
  confidence: InferenceConfidence, matchedOn: string, isWhite: boolean,
): AppellationInference {
  // subregion nuance for Chablis (its own "region-ish")
  return {
    name,
    classification_level: cls,
    country: 'FR',
    region: 'Burgundy',
    subregion: sub,
    confidence,
    matchedOn: `${matchedOn}${isWhite ? ' (white)' : ''}`,
  };
}

/**
 * Infer an appellation from a cuvée string. Never throws; returns a best-effort
 * result with a confidence. Callers should route `confidence === 'low'` to a
 * human review step rather than committing silently.
 */
export function inferAppellation(cuvee: string, opts: InferOptions = {}): AppellationInference {
  const hay = `${norm(cuvee)} | ${norm(opts.producerName)}`;
  const isWhite = norm(opts.style) === 'white';
  const hasGrandCru = /\bgrand cru\b/.test(hay);
  const hasPremier = /1er cru|premier cru|1er\b|1e cru/.test(hay);

  // ── Burgundy ──────────────────────────────────────────────────────────────
  // Precedence is subtle because village names embed Grand Cru names
  // (Chassagne-MONTRACHET is a village; MONTRACHET is the Grand Cru;
  //  Aloxe-CORTON is a village; CORTON is the Grand Cru). Two rules resolve it:
  //   1. An explicit tier keyword ("1er cru" / "grand cru") always wins.
  //   2. Absent a keyword, a commune PREFIX (which a village always carries and
  //      a bare Grand Cru never does) means village; a bare GC name means GC.
  const commune = BURGUNDY_COMMUNES.find(c => c.rx.test(hay));
  const namedGc = BURGUNDY_GRANDS_CRUS.find(gc => gc.rx.test(hay));
  if (commune || namedGc || /\bbourgogne\b|\bburgundy\b/.test(hay)) {
    if (hasPremier && !hasGrandCru) {
      if (commune) return burgundyResult(`${commune.name} 1er Cru`, commune.sub, 'premier_cru', 'high', `commune:${commune.name}+1er`, isWhite);
      return burgundyResult(cuvee.trim(), 'Burgundy', 'premier_cru', 'low', 'premier-no-commune', isWhite);
    }
    if (hasGrandCru) {
      if (namedGc) return burgundyResult(namedGc.name, namedGc.sub, 'grand_cru', 'high', `GC:${namedGc.name}`, isWhite);
      if (commune) return burgundyResult(`${commune.name} Grand Cru`, commune.sub, 'grand_cru', 'medium', `commune:${commune.name}+GC`, isWhite);
      return burgundyResult(cuvee.trim(), 'Burgundy', 'grand_cru', 'low', 'gc-no-name', isWhite);
    }
    // No tier keyword: commune prefix ⇒ village; else bare GC name ⇒ Grand Cru.
    if (commune) return burgundyResult(commune.name, commune.sub, 'village', 'high', `commune:${commune.name}`, isWhite);
    if (namedGc) return burgundyResult(namedGc.name, namedGc.sub, 'grand_cru', 'high', `GC:${namedGc.name}`, isWhite);
    return burgundyResult('Bourgogne', 'Burgundy', 'regional', 'medium', 'bourgogne-generic', isWhite);
  }

  // ── Bordeaux ────────────────────────────────────────────────────────────────
  // Tier note: `cru_bourgeois` used to be the blanket default here, which is
  // factually wrong — Cru Bourgeois is a MÉDOC-ONLY classification and cannot
  // apply to Pomerol (which has no classification at all), Saint-Émilion (its
  // own Grand Cru Classé ladder), Sauternes or Pessac-Léognan. A château's tier
  // is not derivable from an appellation string, so we no longer guess it:
  // communal ACs return `village`, sub-regional ACs return `regional`, and only
  // an explicitly recognised First Growth returns `first_growth`.
  const FIRST_GROWTHS = /lafite|latour\b|mouton|haut[ -]*brion|ch(?:ateau)?[ -]*margaux|petrus|cheval[ -]*blanc|ausone|le[ -]*pin\b/;
  const BORDEAUX_COMMUNES: { rx: RegExp; name: string; bank: 'left' | 'right'; tier: ClassificationLevel }[] = [
    { rx: /pauillac/, name: 'Pauillac', bank: 'left', tier: 'village' },
    { rx: /st[ .-]*julien|saint[ -]*julien/, name: 'Saint-Julien', bank: 'left', tier: 'village' },
    { rx: /st[ .-]*estephe|saint[ -]*estephe/, name: 'Saint-Estèphe', bank: 'left', tier: 'village' },
    { rx: /margaux/, name: 'Margaux', bank: 'left', tier: 'village' },
    { rx: /pessac|leognan|graves/, name: 'Pessac-Léognan', bank: 'left', tier: 'village' },
    { rx: /haut[ -]*medoc/, name: 'Haut-Médoc', bank: 'left', tier: 'regional' },
    { rx: /\bmedoc/, name: 'Médoc', bank: 'left', tier: 'regional' },
    { rx: /pomerol/, name: 'Pomerol', bank: 'right', tier: 'village' },
    { rx: /st[ .-]*emilion|saint[ -]*emilion/, name: 'Saint-Émilion', bank: 'right', tier: 'village' },
    { rx: /fronsac/, name: 'Fronsac', bank: 'right', tier: 'village' },
    { rx: /sauternes/, name: 'Sauternes', bank: 'left', tier: 'village' },
    { rx: /barsac/, name: 'Barsac', bank: 'left', tier: 'village' },
  ];
  for (const b of BORDEAUX_COMMUNES) {
    if (!b.rx.test(hay)) continue;
    const isFirstGrowth = FIRST_GROWTHS.test(hay);
    return {
      name: b.name,
      classification_level: isFirstGrowth ? 'first_growth' : b.tier,
      country: 'FR',
      region: 'Bordeaux',
      // Sauternes/Barsac are geographically Left Bank but are their own world
      // for vintage-chart purposes — keep the sweet subregion explicit.
      subregion: /sauternes|barsac/.test(b.name.toLowerCase())
        ? 'Sauternes'
        : b.bank === 'right' ? 'Right Bank' : 'Left Bank',
      // The appellation itself is stated outright ("Pomerol AC") — that part is
      // certain even though the château's tier is not.
      confidence: 'high',
      matchedOn: `bordeaux:${b.name}${isFirstGrowth ? '+1er-cru-classe' : ''}`,
    };
  }
  if (/bordeaux/.test(hay)) {
    return { name: 'Bordeaux', classification_level: 'regional', country: 'FR', region: 'Bordeaux', confidence: 'medium', matchedOn: 'bordeaux-generic' };
  }

  // ── Italy ───────────────────────────────────────────────────────────────────
  if (/\bbarolo\b/.test(hay)) return { name: 'Barolo', classification_level: 'docg', country: 'IT', region: 'Piedmont', subregion: 'Barolo', confidence: 'high', matchedOn: 'barolo' };
  if (/\bbarbaresco\b/.test(hay)) return { name: 'Barbaresco', classification_level: 'docg', country: 'IT', region: 'Piedmont', subregion: 'Barbaresco', confidence: 'high', matchedOn: 'barbaresco' };
  if (/brunello|montalcino/.test(hay)) return { name: 'Brunello di Montalcino', classification_level: 'docg', country: 'IT', region: 'Tuscany', subregion: 'Montalcino', confidence: 'high', matchedOn: 'brunello' };
  if (/bolgheri|sassicaia|ornellaia|masseto/.test(hay)) return { name: 'Bolgheri', classification_level: 'doc', country: 'IT', region: 'Tuscany', subregion: 'Bolgheri', confidence: 'medium', matchedOn: 'bolgheri' };
  if (/amarone|valpolicella|recioto della valpolicella|quintarelli|dal forno|bertani|masi\b/.test(hay))
    return { name: 'Amarone della Valpolicella', classification_level: 'docg', country: 'IT', region: 'Veneto', subregion: 'Valpolicella', confidence: 'high', matchedOn: 'amarone' };
  if (/franciacorta/.test(hay)) return { name: 'Franciacorta', classification_level: 'docg', country: 'IT', region: 'Lombardy', confidence: 'high', matchedOn: 'franciacorta' };
  if (/tignanello|chianti/.test(hay)) return { name: 'Chianti Classico', classification_level: 'docg', country: 'IT', region: 'Tuscany', confidence: 'low', matchedOn: 'tuscany-igt' };

  // ── Rhône ─────────────────────────────────────────────────────────────────
  if (/hermitage/.test(hay)) return { name: 'Hermitage', classification_level: 'regional', country: 'FR', region: 'Rhône', subregion: 'Northern Rhône', confidence: 'high', matchedOn: 'hermitage' };
  if (/cote[ -]*rotie/.test(hay)) return { name: 'Côte-Rôtie', classification_level: 'regional', country: 'FR', region: 'Rhône', subregion: 'Northern Rhône', confidence: 'high', matchedOn: 'cote-rotie' };
  if (/cornas/.test(hay)) return { name: 'Cornas', classification_level: 'regional', country: 'FR', region: 'Rhône', subregion: 'Northern Rhône', confidence: 'high', matchedOn: 'cornas' };
  if (/chateauneuf/.test(hay)) return { name: 'Châteauneuf-du-Pape', classification_level: 'regional', country: 'FR', region: 'Rhône', subregion: 'Southern Rhône', confidence: 'high', matchedOn: 'cdp' };
  if (/gigondas/.test(hay)) return { name: 'Gigondas', classification_level: 'regional', country: 'FR', region: 'Rhône', subregion: 'Southern Rhône', confidence: 'high', matchedOn: 'gigondas' };

  // ── Champagne ────────────────────────────────────────────────────────────────
  // Grande marque cuvées rarely carry the word "Champagne" — "Cristal",
  // "Dom Pérignon", "Grande Année" all arrive bare. Matching the HOUSE is what
  // makes this high-confidence instead of a low-confidence style guess.
  {
    const byWord     = /champagne/.test(hay);
    const byHouse    = CHAMPAGNE_HOUSES.test(hay);
    const byCuvee    = CHAMPAGNE_CUVEES.test(hay);
    const bySparkling = norm(opts.style) === 'sparkling';
    if (byWord || byHouse || byCuvee || bySparkling) {
      // Sparkling ≠ Champagne — route the other classic methods away first.
      if (/\bcava\b/.test(hay)) return { name: 'Cava', classification_level: 'doc', country: 'ES', region: 'Cava', confidence: 'high', matchedOn: 'cava' };
      if (/prosecco/.test(hay)) return { name: 'Prosecco', classification_level: 'docg', country: 'IT', region: 'Veneto', confidence: 'high', matchedOn: 'prosecco' };
      if (/cremant/.test(hay)) return { name: 'Crémant', classification_level: 'regional', country: 'FR', region: 'France', confidence: 'medium', matchedOn: 'cremant' };
      return {
        name: 'Champagne',
        classification_level: 'regional',
        country: 'FR',
        region: 'Champagne',
        confidence: (byWord || byHouse || byCuvee) ? 'high' : 'low',
        matchedOn: byHouse ? 'champagne:house' : byCuvee ? 'champagne:cuvee' : byWord ? 'champagne:word' : 'champagne:style-only',
      };
    }
  }

  // ── Germany ──────────────────────────────────────────────────────────────────
  // Mosel/Saar/Ruwer Einzellagen almost never say "Mosel" on the label — the
  // vineyard name is the only signal. Without this block every Egon Müller and
  // J.J. Prüm lot fell through to "Unknown region".
  {
    // VDP.Grosse Lage is the German Grand Cru equivalent. The site table is
    // Grosse Lage only, so the tier is safe to assert — and it carries the
    // canonical site name, so the stored appellation is "Wehlener Sonnenuhr"
    // rather than the whole cuvée with its Prädikat attached.
    const grosseLage = MOSEL_GROSSE_LAGE_SITES.find(s => s.rx.test(hay));
    if (grosseLage) {
      return {
        name: grosseLage.name,
        classification_level: 'grand_cru',
        country: 'DE',
        region: 'Mosel',
        subregion: grosseLage.sub,
        confidence: 'high',
        matchedOn: `mosel:grosse-lage:${grosseLage.name}`,
      };
    }
    if (MOSEL_SITES.test(hay) || MOSEL_PRODUCERS.test(hay) || /\bmosel|\bsaar\b|\bruwer\b/.test(hay)) {
      return {
        name: 'Mosel',
        classification_level: 'village',
        country: 'DE',
        region: 'Mosel',
        subregion: /\bsaar\b|wiltinger|ockfener|saarburger|kanzemer|ayler|serriger/.test(hay)
          ? 'Saar'
          : /\bruwer\b|maximin|grunhaus|karthauserhof|eitelsbach|kaseler/.test(hay)
            ? 'Ruwer'
            : 'Mosel',
        confidence: 'medium',
        matchedOn: 'mosel',
      };
    }
    if (/rheingau|johannisberg|rudesheim|erbacher|marcobrunn|steinberg|hochheim|robert weil|schloss vollrads|kunstler/.test(hay))
      return { name: 'Rheingau', classification_level: 'village', country: 'DE', region: 'Rheingau', confidence: 'medium', matchedOn: 'rheingau' };
    if (/\bnahe\b|donnhoff|donnhoff|hermannshohle|schlossbockelheim|emrich[ -]*schonleber|niederhauser/.test(hay))
      return { name: 'Nahe', classification_level: 'village', country: 'DE', region: 'Nahe', confidence: 'medium', matchedOn: 'nahe' };
    if (/\bpfalz\b|forster|deidesheimer|ungeheuer|jesuitengarten|kirchenstuck|burklin|bassermann|christmann|von winning/.test(hay))
      return { name: 'Pfalz', classification_level: 'village', country: 'DE', region: 'Pfalz', confidence: 'medium', matchedOn: 'pfalz' };
    if (/rheinhessen|niersteiner|nackenheim|pettenthal|hipping|rothenberg|keller\b|wittmann|battenfeld/.test(hay))
      return { name: 'Rheinhessen', classification_level: 'village', country: 'DE', region: 'Rheinhessen', confidence: 'medium', matchedOn: 'rheinhessen' };
  }

  // ── Spain ────────────────────────────────────────────────────────────────────
  // Tier note: Spain's DO/DOCa ladder has no dedicated enum member. We reuse the
  // Italian pair as a generic "top-tier / standard controlled appellation"
  // signal: DOCa/DOQ (Rioja, Priorat) → `docg`, DO (Ribera, Rías Baixas) → `doc`.
  // The Crianza/Reserva/Gran Reserva ageing ladder is orthogonal and not a
  // ClassificationLevel — it belongs on the cuvée.
  if (/\brioja\b|tondonia|bosconia|imperial gran reserva|\b(?:89[0o]|904|9o4)\b|la rioja alta|lopez de heredia|\bmuga\b|\bcvne\b|contino|marques de (?:riscal|murrieta)|remelluri|\broda\b|artadi|benjamin romeo|el puntido|vina real/.test(hay))
    return { name: 'Rioja', classification_level: 'docg', country: 'ES', region: 'Rioja', confidence: 'high', matchedOn: 'rioja' };
  if (/ribera del duero|vega sicilia|\bunico\b|valbuena|pingus|pesquera|\baalto\b|\balion\b/.test(hay))
    return { name: 'Ribera del Duero', classification_level: 'doc', country: 'ES', region: 'Ribera del Duero', confidence: 'high', matchedOn: 'ribera' };
  if (/priorat|clos mogador|l[' ]?ermita|alvaro palacios|clos erasmus|clos martinet|finca dofi/.test(hay))
    return { name: 'Priorat', classification_level: 'docg', country: 'ES', region: 'Priorat', confidence: 'high', matchedOn: 'priorat' };
  if (/rias baixas|albarino/.test(hay))
    return { name: 'Rías Baixas', classification_level: 'doc', country: 'ES', region: 'Rías Baixas', confidence: 'medium', matchedOn: 'rias-baixas' };
  if (/\bjerez\b|sherry|oloroso|amontillado|manzanilla|palo cortado|pedro ximenez/.test(hay))
    return { name: 'Jerez', classification_level: 'doc', country: 'ES', region: 'Jerez', confidence: 'high', matchedOn: 'jerez' };

  // ── Portugal ─────────────────────────────────────────────────────────────────
  if (/\bporto\b|\bport\b|taylor|fonseca|graham|dow\b|warre|niepoort|quinta do noval|vintage port/.test(hay))
    return { name: 'Porto', classification_level: 'doc', country: 'PT', region: 'Douro', confidence: 'high', matchedOn: 'porto' };
  if (/madeira|blandy|barbeito|d[' ]?oliveira/.test(hay))
    return { name: 'Madeira', classification_level: 'doc', country: 'PT', region: 'Madeira', confidence: 'high', matchedOn: 'madeira' };

  // ── Tokaj (Hungary) ──────────────────────────────────────────────────────────
  // "Tokay" is the old English/French spelling and MWC still uses it; the
  // Aszú/Puttonyos/Eszencia vocabulary is equally diagnostic on its own.
  if (/tokaj|tokay|aszu|puttonyos|buttonyos|eszencia|essencia|furmint|harslevelu|oremus|royal tokaji|disznoko|szepsy|andrassy|megyer|pajzos/.test(hay)) {
    const puttonyos = /(\d)\s*(?:puttonyos|buttonyos)/.exec(hay)?.[1];
    const eszencia = /eszencia|essencia/.test(hay);
    return {
      name: eszencia ? 'Tokaji Eszencia' : puttonyos ? `Tokaji Aszú ${puttonyos} Puttonyos` : 'Tokaj',
      classification_level: 'regional',
      country: 'HU',
      region: 'Tokaj',
      confidence: 'high',
      matchedOn: eszencia ? 'tokaj:eszencia' : puttonyos ? `tokaj:aszu-${puttonyos}` : 'tokaj',
    };
  }

  // ── Austria ──────────────────────────────────────────────────────────────────
  if (/wachau|kremstal|kamptal|smaragd|federspiel|gruner veltliner|\bfx pichler\b|knoll|prager|hirtzberger|brundlmayer/.test(hay))
    return { name: 'Wachau', classification_level: 'regional', country: 'AT', region: 'Wachau', confidence: 'medium', matchedOn: 'wachau' };

  // ── Loire / Alsace ───────────────────────────────────────────────────────────
  if (/vouvray|montlouis|savenniere|chinon|bourgueil|saumur|sancerre|pouilly[ -]*fume|muscadet|coulee de serrant|huet\b|dagueneau|clos rougeard/.test(hay))
    return { name: 'Loire', classification_level: 'regional', country: 'FR', region: 'Loire', confidence: 'medium', matchedOn: 'loire' };
  if (/alsace|\bzind[ -]*humbrecht\b|trimbach|hugel|weinbach|clos ste[ -]*hune|schlossberg|rangen|brand\b|geisberg/.test(hay))
    return { name: 'Alsace', classification_level: /grand cru/.test(hay) ? 'grand_cru' : 'regional', country: 'FR', region: 'Alsace', confidence: 'medium', matchedOn: 'alsace' };

  // ── Unresolved ──────────────────────────────────────────────────────────────
  return { name: cuvee.trim() || 'Unknown', classification_level: 'generic', country: '', region: '', confidence: 'low', matchedOn: 'none' };
}
